import { stringify } from '@domonda/query-params';
import { Cache } from '../Cache';
import { bludeltaRequest, BluDeltaResponse } from './network';

export interface OCRParams {
  ContainerId: UUID;
  InvoiceId: UUID;
}

// const ocrCache = new Map<string, OCRResponse>();
const ocrCache = new Cache(20);

export interface OCRBoundingBox {
  /**
   * The location and size of a rectangular shape.
   * X, Y, Width and Height seperated by comma.
   *
   * Example: "400, 300, 60, 20"
   * X = 400
   * Y = 300
   * Width = 60
   * Height = 20
   */
  Rectangle: string;
}

export interface OCRWord {
  /** The text-content detected by the OCR. */
  Text: string;
  /** Contains the location and size of the word related to the top-left corner of the first page. */
  BoundingBox: OCRBoundingBox;
  /** Expresses a measure of the capital letter height in pixels. */
  CapHeight: number;
  /**
   * Y coordinate of the baseline in pixels.
   * In case of vertical text this is X coordinate.
   */
  BaseLine: number;
  /** Size of the font in pixels. */
  FontSize: number;
  /** Specifies if the word is formated as "bold". */
  Bold: boolean;
  /** Specifies if the word is formated as "italic". */
  Italic: boolean;
  /** Specifies if the word is formated as "underlined". */
  Underlined: boolean;
  /**
   * Specifies if the font is a proportinal font.
   * A proportinal font is any font whose different characters have different widths.
   */
  Proportional: boolean;
}

export interface OCRLine {
  /** List of words. */
  Words: OCRWord[];
  /** Contains the location and size of the word related to the top-left corner of the first page. */
  BoundingBox: OCRBoundingBox;
}

export interface OCRRegion {
  /** List of lines - A line consists of one or more words on the same baseline in close proximity. */
  Lines: OCRLine[];
  /** Contains the location and size of the word related to the top-left corner of the first page. */
  BoundingBox: OCRBoundingBox;
}

export interface OCRPage {
  /** Page index (0-based). */
  Index: number;
  /** Horizontal resolution of the page in DPI (=dots per inch). */
  DpiX: number;
  /** Vertical resolution of the page in DPI (=dots per inch). */
  DpiY: number;
  /** Width of the page according to the horizontal resolution. */
  Width: number;
  /** Height of the page according to the horizontal resolution. */
  Height: number;
  /**
   * Describes the rotation of the page.
   * Possible values are: "0", "90", "180", "270"
   */
  Orientation: string;
  /** List of regions - A region consists of one or more lines of words in close proximity. */
  Regions: OCRRegion[];
}

export interface OCRResult {
  /** Total width (of all pages) according to the document resolution. */
  Width: number;
  /** Total height (of all pages) according to the document resolution. */
  Height: number;
  /** Total number of pages analyzed by the OCR. */
  NumberOfPages: number;
  /** List of pages. */
  Pages: OCRPage[];
}

export interface OCRResponse extends BluDeltaResponse {
  OcrResult: OCRResult;
}

export async function getOcrResult(params: OCRParams) {
  const key = `${params.ContainerId}-${params.InvoiceId}`;
  if (ocrCache.get(key)) return ocrCache.get(key);
  const queryParams = stringify(params, { prependQuestionMark: true });
  const ocrResult = await bludeltaRequest<OCRResponse>('GET', `/ocrResult${queryParams}`);
  if (typeof ocrResult.OcrResult === 'string') {
    ocrResult.OcrResult = JSON.parse(ocrResult.OcrResult);
  }
  ocrCache.set(key, ocrResult);
  return ocrResult;
}

export interface OCRBoundingBoxPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const extractOcrBoundingBoxRectanglePosition = (rectangle: string, position: string): number => {
  const matches = rectangle.match(new RegExp(`(${position}=)(\\d+)`));
  if (!matches || !matches[2]) {
    throw new Error('Malformed OCRBoundingBox');
  }
  return parseInt(matches[2].trim());
};

export function ocrBoundingBoxToPosition(boundingBox: OCRBoundingBox): OCRBoundingBoxPosition {
  try {
    return {
      top: extractOcrBoundingBoxRectanglePosition(boundingBox.Rectangle, 'Y'),
      left: extractOcrBoundingBoxRectanglePosition(boundingBox.Rectangle, 'X'),
      width: extractOcrBoundingBoxRectanglePosition(boundingBox.Rectangle, 'Width'),
      height: extractOcrBoundingBoxRectanglePosition(boundingBox.Rectangle, 'Height'),
    };
  } catch (error) {
    const [left, top, width, height] = boundingBox.Rectangle.split(',');
    return {
      top: parseInt(top.trim(), 10),
      left: parseInt(left.trim(), 10),
      width: parseInt(width.trim(), 10),
      height: parseInt(height.trim(), 10),
    };
  }
}
